// import { useQuery, useMutation, useQueryClient } from "react-query";
// import {
//   getAllTradingPartners,
//   inviteTradingPartner,
//   getAllPermissions,
//   updateTradingPartnerPermissions,
//   switchContextTradingPartner,
//   getAllClientsForTP,
// } from "../api-services/trading-partners";

// // Hook to fetch all trading partners
// export const useGetAllTradingPartners = (params = {}, options = {}) => {
//   return useQuery({
//     queryKey: ["trading-partners", params],
//     queryFn: () => getAllTradingPartners(params),
//     ...options,
//   });
// };

// // Hook to invite a trading partner
// export const useInviteTradingPartner = (options = {}) => {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: inviteTradingPartner,
//     onSuccess: (data) => {
//       // Invalidate and refetch trading partners list
//       queryClient.invalidateQueries({ queryKey: ["trading-partners"] });

//       // Call custom onSuccess if provided
//       if (options.onSuccess) {
//         options.onSuccess(data);
//       }
//     },
//     ...options,
//   });
// };


// // Hook to switch context for a trading partner
// export const useSwitchContextTradingPartner = (options = {}) => {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: switchContextTradingPartner,
//     onSuccess: (data) => {
//       // Invalidate and refetch trading partners list
//       queryClient.invalidateQueries({ queryKey: ["trading-partners"] });

//       // Call custom onSuccess if provided
//       if (options.onSuccess) {
//         options.onSuccess(data);
//       }
//     },
//     ...options,
//   });
// };


// // Hook to fetch all permissions
// export const useGetAllPermissions = (options = {}) => {
//   return useQuery({
//     queryKey: ["permissions"],
//     queryFn: getAllPermissions,
//     staleTime: 5 * 60 * 1000, // Cache for 5 minutes since permissions don't change often
//     ...options,
//   });
// };


// // Hook to fetch all clients belonging to a trading partner
// export const useGetAllClientsForTP = (params = {}, options = {}) => {
// 	return useQuery({
// 		queryKey: ["trading-partners", params],
// 		queryFn: () => getAllClientsForTP(params),
// 		...options,
// 	});
// };


// // Hook to update trading partner permissions
// export const useUpdateTradingPartnerPermissions = (options = {}) => {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: updateTradingPartnerPermissions,
//     onSuccess: (data) => {
//       // Invalidate and refetch trading partners list
//       queryClient.invalidateQueries({ queryKey: ["trading-partners"] });

//       // Call custom onSuccess if provided
//       if (options.onSuccess) {
//         options.onSuccess(data);
//       }
//     },
//     ...options,
//   });
// };




import { useQuery, useMutation, useQueryClient } from "react-query";

import {
  getAllTradingPartners,
  inviteTradingPartner,
  getAllPermissions,
  updateTradingPartnerPermissions,
  switchContextTradingPartner,
  getAllClientsForTP,
} from "../api-services/trading-partners";

import { tradingPartnerKeys } from "./tradingPartnerKeys";


/**
 * ========================================
 * GET ALL TRADING PARTNERS
 * ========================================
 */
export const useGetAllTradingPartners = (params = {}, options = {}) => {
  return useQuery({
    queryKey: tradingPartnerKeys.list(params),

    queryFn: () => getAllTradingPartners(params),

    keepPreviousData: true,

    staleTime: 30 * 1000,

    refetchOnWindowFocus: false,

    ...options,
  });
};



/**
 * ========================================
 * INVITE TRADING PARTNER
 * ========================================
 */
export const useInviteTradingPartner = (options = {}) => {

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: inviteTradingPartner,

    onSuccess: (newPartner) => {

      /**
       * Instant UI update (optimistic update)
       */
      queryClient.setQueriesData(
        tradingPartnerKeys.lists(),
        (oldData) => {

          if (!oldData) return oldData;

          if (Array.isArray(oldData)) {
            return [newPartner, ...oldData];
          }

          if (oldData?.data) {
            return {
              ...oldData,
              data: [newPartner, ...oldData.data],
            };
          }

          return oldData;
        }
      );

      /**
       * Background sync
       */
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === tradingPartnerKeys.all,
      });

      options.onSuccess?.(newPartner);
    },

    ...options,
  });
};



/**
 * ========================================
 * SWITCH CONTEXT
 * ========================================
 */
export const useSwitchContextTradingPartner = (options = {}) => {

  const queryClient = useQueryClient();

  return useMutation({

    mutationFn: switchContextTradingPartner,

    onSuccess: (data) => {

      queryClient.invalidateQueries({
        queryKey: tradingPartnerKeys.all,
      });

      options.onSuccess?.(data);
    },

    ...options,
  });
};



/**
 * ========================================
 * UPDATE PERMISSIONS
 * ========================================
 */
export const useUpdateTradingPartnerPermissions = (options = {}) => {

  const queryClient = useQueryClient();

  return useMutation({

    mutationFn: updateTradingPartnerPermissions,

    onSuccess: (updatedPartner) => {

      /**
       * Update specific partner instantly
       */
      queryClient.setQueriesData(
        tradingPartnerKeys.lists(),
        (oldData) => {

          if (!oldData?.data) return oldData;

          return {
            ...oldData,
            data: oldData.data.map((partner) =>
              partner.id === updatedPartner.id
                ? updatedPartner
                : partner
            ),
          };
        }
      );

      /**
       * Background refetch
       */
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === tradingPartnerKeys.all,
      });

      options.onSuccess?.(updatedPartner);
    },

    ...options,
  });
};



/**
 * ========================================
 * GET PERMISSIONS
 * ========================================
 */
export const useGetAllPermissions = (options = {}) => {

  return useQuery({

    queryKey: tradingPartnerKeys.permissions(),

    queryFn: getAllPermissions,

    staleTime: 5 * 60 * 1000,

    refetchOnWindowFocus: false,

    ...options,
  });
};



/**
 * ========================================
 * GET CLIENTS FOR TRADING PARTNER
 * ========================================
 */
export const useGetAllClientsForTP = (params = {}, options = {}) => {

  return useQuery({

    queryKey: tradingPartnerKeys.clientList(params),

    queryFn: () => getAllClientsForTP(params),

    keepPreviousData: true,

    staleTime: 30 * 1000,

    refetchOnWindowFocus: false,

    ...options,
  });
};
